import React from "react";
import { Grid } from "antd";
import { Alert } from "../../../../ui/primitives/Alert";
import { Button } from "../../../../ui/primitives/Button";
import { Stack } from "../../../../ui/primitives/Stack";
import { Tag } from "../../../../ui/primitives/Tag";
import { Text } from "../../../../ui/primitives/Text";
import { useTokens } from "../../../../core/theme/TokensProvider";
import { CompletionSection } from "./completion";

interface ProfileCompletionBannerProps {
    sections: CompletionSection[];
    onStart: () => void;
    // Lets the member put the prompt away for this visit. A member who has opted
    // out of direct debit (or simply isn't ready) shouldn't be stuck behind a
    // permanent warning they can't clear — it comes back on their next visit.
    onDismiss: () => void;
}

// Top-of-profile prompt for a member whose profile is incomplete (a guest signup,
// or anyone who abandoned onboarding part-way). It names the outstanding sections
// up front — previously the member had to click around the tabs to discover them —
// and hands them a single "Complete Profile" button that opens a wizard tailored
// to exactly those sections. Renders nothing once everything is done, so the
// banner disappears on the post-save refresh.
const ProfileCompletionBanner: React.FC<ProfileCompletionBannerProps> = ({ sections, onStart, onDismiss }) => {
    const tokens = useTokens();
    const screens = Grid.useBreakpoint();
    // Below md, AntD's Alert squeezes an `action` in beside the message and the
    // heading wraps around it. On a phone the CTA goes full width under the list
    // instead — most members complete their profile on a phone.
    const isNarrow = screens.md === false;

    if (sections.length === 0) return null;

    const cta = (
        <Button
            variant="primary"
            size={isNarrow ? "md" : "sm"}
            block={isNarrow}
            onClick={onStart}
            data-testid="profile-completion-start"
        >
            Complete Profile
        </Button>
    );

    return (
        <Alert
            type="warning"
            showIcon
            closable
            onClose={onDismiss}
            data-testid="profile-completion-banner"
            style={{ marginBottom: tokens.space[4], borderRadius: tokens.radius.md }}
            message={<Text weight="bold">Your profile isn't complete yet</Text>}
            description={
                <Stack gap={2}>
                    <Text>
                        {sections.length === 1
                            ? "There's one section left to complete:"
                            : `There are ${sections.length} sections left to complete:`}
                    </Text>
                    <Stack direction="horizontal" gap={2} wrap>
                        {sections.map((section) => (
                            <Tag
                                key={section.key}
                                tone="warning"
                                style={{ marginInlineEnd: 0, fontWeight: tokens.fontWeight.semibold, whiteSpace: "normal" }}
                                data-testid={`profile-completion-tag-${section.key}`}
                            >
                                {section.title}
                                {/* Spell out which personal fields are missing so the member
                                    knows what they're being asked for before they open the wizard. */}
                                {section.missingItems.length > 0 && `: ${section.missingItems.join(", ")}`}
                            </Tag>
                        ))}
                    </Stack>
                    {isNarrow && cta}
                </Stack>
            }
            action={isNarrow ? undefined : cta}
        />
    );
};

export default ProfileCompletionBanner;
